import React from 'react';

/**
 * Helper: human-readable file size
 */
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Helper: pick a small icon for a file type
 */
const fileIcon = (name = '', contentType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (contentType.startsWith('video/') || ['mp4', 'mov', 'avi', 'webm'].includes(ext)) return '🎬';
  if (contentType.startsWith('audio/') || ['mp3', 'wav', 'aac', 'm4a'].includes(ext)) return '🎵';
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return '📊';
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return '📊';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '🗜️';
  if (['ai', 'psd', 'sketch', 'fig', 'xd'].includes(ext)) return '🎨';
  return '📄';
};

const ProviderCompletionForm = ({
  completionNotes,
  setCompletionNotes,
  proofPreviews,
  proofImages,
  uploadingProof,
  onProofUpload,
  onRemoveProof,
  proofFiles = [],
  uploadingFile = false,
  onProofFileUpload,
  onRemoveProofFile,
  onRequestCompletion,
  loading,
  requestDisabled = false,
}) => {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold">Mark Job as Complete</h4>

      {requestDisabled && (
        <div className="p-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg text-sm" data-testid="end-job-required-hint">
          You've started the live service tracking. Please tap <strong>"End Job"</strong> above to stop the timer before requesting completion.
        </div>
      )}

      {/* Completion Notes */}
      <textarea
        value={completionNotes}
        onChange={(e) => setCompletionNotes(e.target.value)}
        placeholder="Add any notes about the completed work (optional)"
        rows="3"
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="completion-notes"
      />

      {/* Proof Images Upload */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <label className="block font-medium text-gray-700 mb-2">
          Upload Proof Photos (Optional, up to 5 photos)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Take or upload photos showing the completed work
        </p>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
          {proofPreviews.map((preview, index) => (
            <div key={index} className="relative group" data-testid={`proof-image-${index}`}>
              <img
                src={preview}
                alt={`Proof ${index + 1}`}
                className="w-full h-20 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => onRemoveProof(index)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
                data-testid={`remove-proof-${index}`}
              >
                x
              </button>
            </div>
          ))}

          {proofImages.length < 5 && (
            <label className="border-2 border-dashed border-gray-300 rounded-lg h-20 flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition" data-testid="add-proof-btn">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                capture="environment"
                onChange={onProofUpload}
                className="hidden"
                disabled={uploadingProof}
              />
              {uploadingProof ? (
                <span className="text-gray-500 text-xs">Uploading...</span>
              ) : (
                <>
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                </>
              )}
            </label>
          )}
        </div>

        {proofImages.length === 0 && (
          <p className="text-xs text-gray-400 italic">No proof images uploaded yet</p>
        )}
      </div>

      {/* Generic File Upload (non-image deliverables) */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <label className="block font-medium text-gray-700 mb-2">
          Upload Deliverable Files (Optional, up to 5 files · 20 MB each)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          For non-photo deliverables: PDF, DOC/DOCX, ZIP, AI/PSD, MP4, audio, etc.
        </p>

        <ul className="space-y-2 mb-3">
          {proofFiles.map((f, index) => (
            <li
              key={index}
              className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2"
              data-testid={`proof-file-${index}`}
            >
              <span className="text-2xl">{fileIcon(f.original_name, f.content_type)}</span>
              <div className="flex-1 min-w-0">
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm font-medium text-gray-800 hover:text-orange-600"
                  title={f.original_name}
                >
                  {f.original_name || 'file'}
                </a>
                <p className="text-xs text-gray-400">{formatBytes(f.size_bytes)}</p>
              </div>
              <button
                type="button"
                onClick={() => onRemoveProofFile && onRemoveProofFile(index)}
                className="text-red-500 hover:text-red-700 text-sm font-medium"
                data-testid={`remove-proof-file-${index}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        {(proofFiles.length < 5) && (
          <label
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg cursor-pointer hover:bg-gray-700 transition text-sm font-medium disabled:opacity-50"
            data-testid="add-proof-file-btn"
          >
            <input
              type="file"
              onChange={onProofFileUpload}
              className="hidden"
              disabled={uploadingFile}
            />
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            {uploadingFile ? 'Uploading…' : 'Attach a file'}
          </label>
        )}

        {proofFiles.length === 0 && !uploadingFile && (
          <p className="text-xs text-gray-400 italic mt-2">No files attached yet</p>
        )}
      </div>

      <button
        onClick={onRequestCompletion}
        disabled={loading || uploadingProof || uploadingFile || requestDisabled}
        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="request-completion-btn"
      >
        {loading ? 'Sending...' : requestDisabled ? 'End Job first to request completion' : 'Request Completion Confirmation'}
      </button>
    </div>
  );
};

export default ProviderCompletionForm;
